import { Component, inject, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { filter, Subscription } from 'rxjs';
import { RootCauseFollowupService } from 'src/app/services/root-cause-followup.service';
import { FollowupEntryComponent } from './followup-entry/followup-entry.component';
import { ChangeStatus } from 'src/app/services/problem.service';
import { MatButtonModule } from '@angular/material/button';
import { GeaMesDatepickerModule } from '@gea-mes/datepicker';
import dayjs from 'dayjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AdminService, PlantType } from 'src/app/admin/services/admin.service';

@Component({
  selector: 'app-root-cause-followup',
  imports: [ReactiveFormsModule, MatCardModule, MatTableModule, FollowupEntryComponent, MatButtonModule, GeaMesDatepickerModule, MatFormFieldModule, MatSelectModule, MatCheckboxModule],
  templateUrl: './root-cause-followup.component.html',
  styleUrl: './root-cause-followup.component.scss'
})
export class RootCauseFollowupComponent implements OnInit {

  /*******************************************************
  * Service Injections                                   *
  ********************************************************/
  protected svcRootCause = inject(RootCauseFollowupService);
  protected svcAdmin = inject(AdminService);

  /*******************************************************
  * Class Variables                                      *
  ********************************************************/

  protected fullScreenDisplayedColumns: string[] = ['Hour','Fault','Duration','RootCause','Acknowledge'];
  protected displayedColumns: string[] = []; // Hour is removed in ngOninit() if looking at just a single hour
  
  protected faultDataSub$: Subscription | undefined;
  protected faultListData:any[] = [];
  protected displayFaultListData:any[] = [];

  protected editMode:boolean = false;
  protected editStatus:ChangeStatus = ChangeStatus.NO_CHANGES;
  protected ChangeStatusEnum = ChangeStatus;
  protected changeSub$: Subscription | undefined;
  protected saveInProgress:boolean = false;
  protected faultListCurrent:boolean = false;


  protected plantList: any[] = [];
  protected plantSelect = new FormControl();

  protected rootCauseList: any[] = [];
  protected rootCauseSelect = new FormControl();

  protected startDate:string | undefined;
  protected endDate:string | undefined;
  protected shifts:any[] = ['1ST SHIFT','2ND SHIFT','3RD SHIFT'];
  protected shiftSelect = new FormControl(['1ST SHIFT','2ND SHIFT','3RD SHIFT']);
  protected hideReviewComplete = new FormControl(true); // Default to true (or false based on your preference)



  ngOnInit(): void {
    this.displayedColumns = this.fullScreenDisplayedColumns;
    this.getSiteObjectList();
  }

  getSiteObjectList() {
    this.svcAdmin.plantList().subscribe((
      out => {
        this.plantList = out.Body;
        this.rootCauseSelect.reset();
      }
    ));
  }

  getRootCauses() {
    if (this.plantSelect.value) {
      this.svcRootCause.getRootCause(this.plantSelect.value).pipe(filter(out => out != null)).subscribe(
        (out) => {
          this.rootCauseList = out.Body;
        }
      );
    }
  }

  getData(): void {
    if (this.plantSelect.value && this.rootCauseSelect.value && this.startDate && this.endDate) {
      if (this.faultDataSub$) this.faultDataSub$.unsubscribe();

      this.faultDataSub$ = this.svcRootCause.getFaultList(this.plantSelect.value, this.rootCauseSelect.value,this.startDate, this.endDate).pipe(filter(out => out != null)).subscribe(
        out => {
          console.log("Faults", out);
          this.faultListData = out.Body;
          this.filter();
          this.faultListCurrent = true;
        }
      );

    }
  }

  filter() {
    let filteredFaults:any; 

    // Filter out only shifts we want to display
    filteredFaults = this.faultListData.filter(
      item => {
        // Does the record match the shifts selected
        const matchesShift =  this.shiftSelect.value?.includes(item.Shift);

        // Hide/Show complete records
        const isCompleted = item.AcknowledgeDateTime && item.AcknowledgeDateTime.trim() !== '';
        const matchesReviewStatus = !(this.hideReviewComplete.value && isCompleted);

        return matchesShift && matchesReviewStatus;
      }
    );
  


    this.displayFaultListData = filteredFaults; //this.faultListData;
  }

  onDateSelection(e) {
    this.startDate = dayjs(e.start).format('YYYY-MM-DD'); 
    this.endDate = dayjs(e.end).format('YYYY-MM-DD'); 
    this.getData();
  }

  formatDuration(seconds:number) {
    return new Date(seconds * 1000).toISOString().slice(11, 19);
  }

  toggleEditMode() {
    this.editMode = !this.editMode

    if (this.editMode) {
      if (this.changeSub$) this.changeSub$.unsubscribe();

      this.changeSub$ = this.svcRootCause.getChangeStatus().subscribe(
        (status:ChangeStatus) => {
          this.editStatus = status;

          if (this.saveInProgress) {
            if (status == ChangeStatus.NO_CHANGES) {
              this.saveInProgress = false;
              this.getData();
            }
          } 
        }
      )
     } else {
      this.faultListCurrent = false; // Fault list data should no longer be considered current.
      this.saveInProgress = true;
      this.faultListData = [];
      this.svcRootCause.setSavetrigger(true); // Trigger unsaved changes to be saved. TODO: The screen may display old versions until save is complete, should clean that up.

      // If no changes, then get fault list
      if (this.editStatus <= ChangeStatus.NO_CHANGES) {
        this.getData();
      }
    }
  }
}
